/* user/sagas*/
import {
  select,
  all,
  fork,
  put,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { has, isNil, includes, isEmpty } from "ramda";
import { User, UserActionTypes } from "./types";
import { fetch, save, drop, fetchPermission } from "../../repo/user";
import {
  fetchRequest,
  fetchError,
  fetchSuccess,
  saveRequest,
  saveSuccess,
  saveError,
  deleteRequest,
  deleteSuccess,
  deleteError, fetchPermissionSuccess,
} from "./actions";
import { dirty } from "../session/actions";
import { Error, isAuthError } from "../";

function* handleFetch(action: ReturnType<typeof fetchRequest>) {
  try {
    const user = yield fetch(action.payload.id, action.payload.params);
    if (user.errors && !isEmpty(user.errors)) {
      if (isAuthError(user.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(user.errors)));
    } else {
      yield put(fetchSuccess(user));
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* handleFetchPermission(action: ReturnType<typeof fetchRequest>) {
  try {
    const user = yield fetchPermission(action.payload.id, action.payload.params);
    if (user.errors && !isEmpty(user.errors)) {
      if (isAuthError(user.errors)) {
        yield put(dirty());
      }
      yield put(fetchError(JSON.stringify(user.errors)));
    } else {
      yield put(fetchPermissionSuccess(user));
    }
  } catch (err) {
    yield put(fetchError(err));
  }
}

function* watchFetchRequest() {
  yield takeEvery(UserActionTypes.FETCH, handleFetch);
}

function* watchFetchPermissionRequest() {
  yield takeEvery(UserActionTypes.FETCH_PERMISSION, handleFetchPermission);
}

function* handleSave(action: ReturnType<typeof saveRequest>) {
  try {
    const user = yield save(action.payload.data);
    // we may still have an error
    // so then we should yield error
    if (user.errors && !isEmpty(user.errors)) {
      if (isAuthError(user.errors)) {
        yield put(dirty());
      }
      yield put(saveError(JSON.stringify(user.errors)));
      
      if (action.payload.callback) {
        // Try to extract specific error messages for better user experience
        const errorDetail = user.errors[0]?.detail;
        let errorMessage = typeof errorDetail === "string" ? errorDetail : JSON.stringify(errorDetail);

        // Just return the error message as provided by Auth0
        action.payload.callback(errorMessage, null);
      }
    } else {
      yield put(saveSuccess(user));
      action.payload.callback && action.payload.callback(null, user);
    }
  } catch (err) {
    yield put(saveError(err));
    
    if (action.payload.callback) {
      // Handle network or other errors
      let errorMessage = "An error occurred while saving User";
      
      // Check if it's an Auth0 error
      if (err && err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      
      action.payload.callback(errorMessage, null);
    }
  }
}

function* watchSaveRequest() {
  yield takeEvery(UserActionTypes.SAVE_REQUEST, handleSave);
}

function* handleDelete(action: ReturnType<typeof deleteRequest>) {
  try {
    const dropResponse = yield drop(action.payload.id);

    if (dropResponse.errors && !isEmpty(dropResponse.errors)) {
      if (isAuthError(dropResponse.errors)) {
        yield put(dirty());
      }
      yield put(deleteError(JSON.stringify(dropResponse.errors)));
      action.payload.callback &&
        action.payload.callback(JSON.stringify(dropResponse.errors));
    } else {
      yield put(deleteSuccess());
      action.payload.callback && action.payload.callback(null);
      yield put(fetchRequest(null));
    }
  } catch (err) {
    yield put(deleteError(err));
    action.payload.callback && action.payload.callback(JSON.stringify(err));
  }
}

function* watchDeleteRequest() {
  yield takeLatest(UserActionTypes.DELETE_REQUEST, handleDelete);
}

export default function* userSaga() {
  yield all([
    fork(watchFetchRequest),
    fork(watchSaveRequest),
    fork(watchDeleteRequest),
    fork(watchFetchPermissionRequest),
  ]);
}
